import GetAppIcon from '@material-ui/icons/GetApp';
import PrintIcon from '@material-ui/icons/Print';
import { amortizationOptions } from '@roc/feature-loans';
import {
  DateFormat,
  formatDate,
  isNil,
  LoanSubType,
  PURCHASE,
  REFINANCE,
} from '@roc/feature-utils';
import { Button, PrintableAreaRef, StandardDialog } from '@roc/ui';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { rentalExperienceOptions } from 'libs/feature-borrower-loan-submission/src/modules/newLoanApplication/utils/loanApplicationConstants';
import { swapIndexLabels } from 'libs/feature-loans/src/rentalPricer/pricerSummary/utils/constants';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { useLoanTypeStore } from '../../../hooks/useLoanTypeStore';
import { ltvMapper } from '../../../stores/singleProperty/common/pricerSummaryHelper';
import { SinglePropertyQuoteStore } from '../../../stores/singleProperty/singlePropertyQuoteStore';
import { DEFAULT_FEE_CORELOGIX, DEFAULT_FEE_LEGAL_REVIEW } from '../../../utils/constants';
import { DownloadableLoanRatesHtml } from './downloadableLoanRatesHtml';

const DOCUMENT_NAME = 'Loan Rates.pdf';

interface Props {
  open?: boolean;
  isSubmittedQuote?: boolean
  onClose?: () => void;
}

const _formatCurrency = val => {
  return isNil(val) ? '-' : formatCurrency(val);
};

const _formatDate = (val, format) => {
  return isNil(val) ? '-' : formatDate(val, format);
};

const _formatPercentage = val => {
  return isNil(val) ? '-' : formatPercentage(val);
};

const getTodayGrossRate = option => {
  const { spreadRate, swapRate } = option.rateRow?.[option.ltvBand] || {};
  return spreadRate && swapRate ? `${(spreadRate + swapRate).toFixed(4)}%` : '';
};

const getIndexLabel = quote => {
  const ltvBand = ltvMapper[quote.loanToValue];
  const swapIndex =
    quote?.pricerLoanTerms?.loanTerms?.[quote.rateType]?.[quote.amortization]
      ?.baseRates?.[ltvBand]?.swapIndex;
  return swapIndexLabels[swapIndex];
};

export const DownloadableLoanRatesDialog = observer((props: Props) => {
  const { open, onClose, isSubmittedQuote } = props;
  const downloadableLoanRatesRef = useRef<PrintableAreaRef>(null);
  const { loanTypeStore } = useLoanTypeStore();
  const singlePropertyQuoteStore = loanTypeStore as SinglePropertyQuoteStore;
  const { loanEconomicsStore } = singlePropertyQuoteStore;

  const property = singlePropertyQuoteStore.singlePropertyPropertyStore.propertyFormStore.getFormValues();
  const isPurchase = !property.propertyOwnership;
  const loanPurpose = isPurchase ? PURCHASE : REFINANCE;
  const quotes = isSubmittedQuote ? [loanEconomicsStore?.quote] : loanEconomicsStore.selectedQuotes;

  const {
    mainBorrowerFormStore,
    coBorrowersFormStores,
  } = singlePropertyQuoteStore.borrowerInformationStore;
  const borrowers = [
    mainBorrowerFormStore.getFormValues(),
    ...coBorrowersFormStores.map(f => f.getFormValues()),
  ];

  const loanRatesData = {
    loanSubtype: LoanSubType.SINGLE_PROPERTY,
    borrowers: borrowers.map(borrower => ({
      firstName: borrower.firstName,
      lastName: borrower.lastName,
      ficoScore: borrower.ficoProvidedAtOrigination,
      experience: rentalExperienceOptions.find(opt => opt.value === borrower.rentalExperience)?.label,
    })),
    properties: [
      {
        loanPurpose,
        address: singlePropertyQuoteStore.singlePropertyPropertyStore.buildFullAddress(),
        purchasePrice: _formatCurrency(property.purchasePrice),
        purchaseDate: _formatDate(property.purchaseDate, DateFormat.MMDDYYYY),
        estimatedPayoffAmount: _formatCurrency(property.payoffAmount),
        completedRenoToDate: !isPurchase
          ? _formatCurrency(property.renoBudget)
          : '',
        totalRenovationBudget: '',
        monthlyMarketRent: _formatCurrency(property.monthlyMarketRent),
        propertyType: property.propertyType,
        numberOfUnits: 1,
        estimatedAsIsValue: _formatCurrency(property.currentMarketValue),
        annualTaxes: _formatCurrency(property.annualTaxes),
        annualInsurance: _formatCurrency(property.annualInsurance),
        annualHOA: _formatCurrency(property.annualHOA),
      },
    ],
    pricingOptions: quotes.map(quote => ({
      loanPurpose: loanPurpose,
      loanToValue: _formatPercentage(quote.loanToValue),
      loanAmount: _formatCurrency(quote.pricerLoanTerms.amount),
      amortization: amortizationOptions.find(
        opt => opt.value == quote.amortization
      )?.label,
      prePaymentPenalty: quote.prePaymentPenalty,
      rateType: quote.rateType,
      index: getIndexLabel(quote),
      todayGrossRate: _formatPercentage(quote?.rate),
      todayDSCR: quote?.dscr?.pitiaDscrInIOPeriod,
      monthlyPIPayment: _formatCurrency(
        quote.pricerLoanTerms.monthlyPaymentWithoutExpenses
      ),
      monthlyPITIAPayment: _formatCurrency(
        quote?.pricerLoanTerms?.monthlyPayment
      ),
      postLoanApprovalRateLock: '-',
      corelogixFee: _formatCurrency(DEFAULT_FEE_CORELOGIX),
      legalFee: _formatCurrency(DEFAULT_FEE_LEGAL_REVIEW),
      pointsAndFeesPercent: _formatPercentage(quote?.pointsAndFeesPercent + quote?.addOnPoints),
      adminFee: _formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.adminFees),
      commitmentFee: _formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.commitmentFees),
      processingFee: _formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.processingFees),
      underwritingFee: _formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.underwriterFees),
      buydownFee: _formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.buydownFees),
    })),
    fiveYrTsrySwap: _formatPercentage(quotes?.[0]?.fiveYrTsrySwap),
    tenYrTsrySwap: _formatPercentage(quotes?.[0]?.tenYrTsrySwap),
  };

  const download = () => {
    downloadableLoanRatesRef.current.downloadAsPdf(DOCUMENT_NAME);
  };

  const print = async () => {
    const url = await downloadableLoanRatesRef.current.getPdfBlobUrl();
    const printWindow = window.open(url.toString(), '_blank');
    printWindow?.print();
  };

  const dialogContent = loanRatesData ? (
    <DownloadableLoanRatesHtml
      ref={downloadableLoanRatesRef}
      data={loanRatesData}
    />
  ) : null;

  const dialogActions = (
    <>
      <Button
        style={{ marginRight: '8px' }}
        testId="print-pdf"
        color="default"
        variant="contained"
        onClick={print}
        startIcon={<PrintIcon />}
      >
        Print
      </Button>
      <Button
        testId="download-pdf"
        color="primary"
        variant="contained"
        startIcon={<GetAppIcon />}
        onClick={download}
      >
        Download
      </Button>
    </>
  );

  return (
    <StandardDialog
      open={open}
      title="Loan Rates"
      maxWidth="md"
      handleClose={onClose}
      dialogActions={dialogActions}
      dialogContent={dialogContent}
    />
  );
});
